import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { MediaItem, Region, RoomRegionState } from "@gooner/shared";
import { capRung, pickRenditionHeight, rungUrl, tileSourceHeight } from "@gooner/shared";
import { mediaErrorMessage, track } from "../analytics";
import { isSafeMode } from "../lib/safe-mode";
import { watchBegin, watchEnd } from "../lib/watchReport";
import type { SyncBus, SyncState } from "./syncBus";
import type { RegionController, RegionRegistry } from "./types";
import { useAdaptiveFraming } from "./useAdaptiveFraming";
import { budgetFor, deviceDpr } from "./playbackBudget";
import { useOnscreenRef, useTileOnscreen } from "./useTileOnscreen";

/** The crossfade between clips — RegionPlayer's, so a room's tile dissolves like a wall's. */
const FADE_MS = 600;
const FRAMING_MOVE_MS = 700;
/** How far a driven tile may be from the host's position before a resume re-seeks it. */
const RESUME_DRIFT_S = 1.5;

/**
 * A GOON ROOM MEMBER's region: it plays whatever the HOST's region of the same id is
 * playing, told over the socket (useRoom → regionsBus), and NOTHING of its own — no
 * feed, no clip selection, no advance timer. The host's tile is the clock: a reveal
 * arrives with the server's `startedAt`, this tile seeks to (serverNow − startedAt) on
 * its first playable frame and then plays on, looping, until the next reveal. The sync
 * is approximate by design — RTT plus first-frame decode, re-aligned on every clip,
 * which on 3–30 s clips is a second or two and invisible in a room.
 *
 * Built on SyncedRegion's follower shape (seed from `bus.get`, subscribe, seek) without
 * its captureStream: the leader here is in another browser. Two stacked slots keyed on
 * the reveal's `seq`, the incoming one revealed on `canplay` with the wall's crossfade,
 * so a clip change never shows a black flash.
 *
 * Its OWN rendition, not the host's: a phone in a room would otherwise pull the host's
 * 1080 rung for a 300 px tile, and bandwidth is this app's bottleneck. The host's `src`
 * is the fallback when a rung fails to load. Registers a RegionController so the crown,
 * the hover ring and Play-1 read the dictated clip as `current()`; `advance` is a no-op
 * — a member does not skip, the host does. Publishes to the LOCAL SyncBus as a leader
 * would, so a synced follower tile beside it (a mirrored pair in the host's layout)
 * still has something to follow.
 */
export function DrivenRegion({
  region,
  bus,
  serverOffset,
  registry,
  syncBus,
  paused,
  muted,
  volume,
  onView,
}: {
  region: Region;
  bus: SyncBus<RoomRegionState>;
  serverOffset: number;
  registry: RegionRegistry;
  syncBus: SyncBus<SyncState>;
  paused: boolean;
  muted: boolean;
  volume: number;
  onView: (item: MediaItem) => void;
}) {
  type Slot = { st: RoomRegionState; visible: boolean };
  // Up to two: the one on screen and the one buffering under it (or fading out over it).
  const [slots, setSlots] = useState<Slot[]>(() => {
    const st = bus.get(region.id);
    return st ? [{ st, visible: false }] : [];
  });
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [renditionsOk, setRenditionsOk] = useState(true);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const videoRefs = useRef(new Map<number, HTMLVideoElement>());
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const watchSlot = useId();
  const serverOffsetRef = useRef(serverOffset);
  serverOffsetRef.current = serverOffset;
  const onViewRef = useRef(onView);
  onViewRef.current = onView;

  const visible = slots.find((s) => s.visible) ?? null;
  const pending = slots.find((s) => !s.visible) ?? null;
  const visibleRef = useRef<Slot | null>(visible);
  visibleRef.current = visible;
  const pendingRef = useRef<Slot | null>(pending);
  pendingRef.current = pending;

  // The tile's box, for the rung. Measured off the wrapper, which exists before any
  // element does — a video mounted before the box is known would fetch the master.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      // Real pixels at the capped ratio, the same measure the wall uses (deviceDpr, #13).
      const dpr = deviceDpr().effective;
      const next = { w: Math.round(el.clientWidth * dpr), h: Math.round(el.clientHeight * dpr) };
      if (!next.w || !next.h) return;
      setBox((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const onscreen = useTileOnscreen(wrapRef);
  const onscreenRef = useOnscreenRef(onscreen);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const still = paused || !onscreen;
  const stillRef = useRef(still);
  stillRef.current = still;

  // Follow the host. A state with a seq this tile already holds is a rejoin's echo.
  useEffect(() => {
    const take = (st: RoomRegionState | null) => {
      setSlots((cur) => {
        if (!st) return [];
        if (cur.some((s) => s.st.seq === st.seq)) return cur;
        const shown = cur.filter((s) => s.visible);
        return [...shown, { st, visible: false }];
      });
    };
    take(bus.get(region.id));
    return bus.subscribe(region.id, take);
  }, [bus, region.id]);

  useEffect(
    () => () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      watchEnd(watchSlot);
      syncBus.clear(region.id);
    },
    [watchSlot, syncBus, region.id],
  );

  /** Where the host is in this clip right now, in seconds, folded into its length. */
  const hostPosition = useCallback((st: RoomRegionState, duration: number | undefined): number => {
    const elapsed = (Date.now() + serverOffsetRef.current - st.startedAt) / 1000;
    if (!(elapsed > 0)) return 0;
    const d = duration && Number.isFinite(duration) && duration > 0 ? duration : st.item.durationSec;
    return d && d > 0 ? elapsed % d : elapsed;
  }, []);

  /** The incoming slot can play: land on the host's position and bring it up. */
  const reveal = useCallback(
    (seq: number) => {
      const slot = pendingRef.current;
      if (!slot || slot.st.seq !== seq) return;
      const v = videoRefs.current.get(seq);
      if (v && Number.isFinite(v.duration)) v.currentTime = hostPosition(slot.st, v.duration);
      if (v && !stillRef.current) void v.play().catch(() => undefined);
      const outgoing = visibleRef.current;
      setSlots((cur) => cur.map((s) => (s.st.seq === seq ? { ...s, visible: true } : s)));
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => {
        // The outgoing clip has faded under the new one; let it go. A slot that arrived
        // meanwhile (still hidden) stays: it is the next reveal.
        setSlots((cur) => cur.filter((s) => s.st.seq === seq || !s.visible));
        if (outgoing) videoRefs.current.delete(outgoing.st.seq);
      }, FADE_MS);
      syncBus.publish(region.id, { item: slot.st.item, src: slot.st.src, startedAt: Date.now(), video: v ?? null });
    },
    [hostPosition, watchSlot, syncBus, region.id],
  );

  // Hold to the wall's pause and the tile's visibility. Coming back from still, the
  // element sat where it stopped while the host played on: re-seek before resuming
  // when the gap is past what a room notices.
  useEffect(() => {
    const slot = visibleRef.current;
    const v = slot ? videoRefs.current.get(slot.st.seq) : undefined;
    if (!v) return;
    if (still) {
      v.pause();
      return;
    }
    if (slot && Number.isFinite(v.duration)) {
      const want = hostPosition(slot.st, v.duration);
      if (Math.abs(v.currentTime - want) > RESUME_DRIFT_S) v.currentTime = want;
    }
    void v.play().catch(() => undefined);
  }, [still, visible?.st.seq, hostPosition]);

  // A clip counts when it is watched, not when it is loaded: a neighbouring page of a
  // room's scroll reveals off screen (RegionPlayer's syncWatch has the whole story).
  const countedSeqRef = useRef(-1);
  const liveItem = !still && visible ? visible.st.item : null;
  const liveSeq = !still && visible ? visible.st.seq : -1;
  useEffect(() => {
    if (!liveItem) {
      watchEnd(watchSlot);
      return;
    }
    watchBegin(watchSlot, liveItem);
    if (countedSeqRef.current !== liveSeq) {
      countedSeqRef.current = liveSeq;
      onViewRef.current(liveItem);
    }
    // liveItem is the slot's own, so it only changes along with liveSeq.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSeq, watchSlot]);

  // A tab that comes back after minutes away is a clip's length out of step.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const slot = visibleRef.current;
      const v = slot ? videoRefs.current.get(slot.st.seq) : undefined;
      if (!slot || !v || !Number.isFinite(v.duration)) return;
      v.currentTime = hostPosition(slot.st, v.duration);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [hostPosition]);

  // Sound: the verdict is the host's (the stage's soundsHere); the level is this screen's.
  useEffect(() => {
    for (const v of videoRefs.current.values()) {
      v.muted = muted;
      v.volume = volume;
    }
  }, [muted, volume, visible?.st.seq]);

  // The controller the screen's chrome reaches this tile through.
  useEffect(() => {
    const ctrl: RegionController = {
      pause: () => {
        pausedRef.current = true;
        const s = visibleRef.current;
        const v = s ? videoRefs.current.get(s.st.seq) : undefined;
        v?.pause();
      },
      resume: () => {
        pausedRef.current = false;
        if (!onscreenRef.current) return;
        const s = visibleRef.current;
        const v = s ? videoRefs.current.get(s.st.seq) : undefined;
        if (v) void v.play().catch(() => undefined);
      },
      // A member never skips: the host's tile is the one that advances.
      advance: () => undefined,
      current: () => visibleRef.current?.st.item ?? null,
      onGlass: () => onscreenRef.current,
      pending: () => pendingRef.current?.st.item ?? null,
      audible: () => {
        const it = visibleRef.current?.st.item;
        return !!it && it.kind === "video" && it.audible !== false;
      },
    };
    registry.set(region.id, ctrl);
    return () => {
      registry.delete(region.id);
    };
  }, [registry, region.id, onscreenRef]);

  const fit = region.fit === "contain" ? "object-contain" : "object-cover";

  // Adaptive Framing, both slots — the outgoing one is still visible through the fade.
  const frontRef = useRef<HTMLVideoElement | null>(null);
  const backRef = useRef<HTMLVideoElement | null>(null);
  frontRef.current = visible ? (videoRefs.current.get(visible.st.seq) ?? null) : null;
  backRef.current = pending ? (videoRefs.current.get(pending.st.seq) ?? null) : null;
  useAdaptiveFraming(
    [
      { ref: frontRef, item: visible?.st.item ?? null, epoch: visible?.st.seq },
      { ref: backRef, item: pending?.st.item ?? null, epoch: pending?.st.seq },
    ],
    region,
    box,
    FRAMING_MOVE_MS,
  );

  /** This tile's own rung of the host's clip; the host's url once a rung has failed. */
  const srcFor = (st: RoomRegionState): string | null => {
    if (!box) return null;
    if (!renditionsOk || st.item.sourceKind === "upload") return st.src;
    // Under the display's ceiling (phone setting, probe, live budget). The host decides what
    // plays here, so a Rooms tile takes the ceiling but never a decode slot.
    return rungUrl(
      st.item.url,
      capRung(pickRenditionHeight(tileSourceHeight(box, st.item, region.fit)), budgetFor(registry).ceiling()),
    );
  };

  const renderSlot = (slot: Slot, z: number) => {
    const it = slot.st.item;
    const seq = slot.st.seq;
    const src = srcFor(slot.st);
    if (!src) return null;
    return (
      <div
        key={seq}
        className="absolute inset-0"
        style={{ zIndex: z, opacity: slot.visible ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
      >
        {it.kind === "video" ? (
          <video
            ref={(v) => {
              if (v) {
                videoRefs.current.set(seq, v);
                v.volume = volume;
                if (stillRef.current) v.pause();
              } else videoRefs.current.delete(seq);
            }}
            className={`absolute inset-0 h-full w-full ${fit}`}
            src={src}
            poster={it.posterUrl}
            autoPlay
            playsInline
            // Loops: the host's tile decides when this clip is over, by revealing the next.
            loop
            muted={muted}
            controls={false}
            disablePictureInPicture={isSafeMode()}
            onCanPlay={() => {
              if (!slot.visible) reveal(seq);
              else if (stillRef.current) videoRefs.current.get(seq)?.pause();
            }}
            onError={(e) => {
              // A rung that will not load: fall back to the host's own url for this and
              // every later clip; a clip that fails on that too waits for the next reveal.
              if (renditionsOk && src !== slot.st.src) setRenditionsOk(false);
              else {
                const err = e.currentTarget.error;
                track("media_error", {
                  ...budgetFor(registry).eventProps(),
                  surface: "room",
                  code: err?.code ?? null,
                  message: mediaErrorMessage(err),
                });
              }
            }}
          />
        ) : (
          <img
            className={`absolute inset-0 h-full w-full ${fit}`}
            src={src}
            alt=""
            draggable={false}
            onLoad={() => {
              if (!slot.visible) reveal(seq);
            }}
          />
        )}
      </div>
    );
  };

  // Black while nothing has arrived: this tile has nothing of its own to load, so a
  // spinner would promise activity that is not happening here.
  return (
    <div ref={wrapRef} className="absolute inset-0 overflow-hidden bg-black">
      {/* Later is higher: a reveal appends, so the incoming clip fades in OVER the outgoing. */}
      {slots.map((s, i) => renderSlot(s, i + 1))}
    </div>
  );
}
